import { CandyJarShow, CJPromoShelfSeries } from '@inkitt/types-galatea';
import { languageCodesSchema } from '@inkitt/types-galateatv';
import { z } from 'zod';
import { c } from '../../contract';

/** Reading milestone that grants the repeatable in-reader adapted-book coins (DEV-5282). */
export const adaptedBookCoinsMilestoneSchema = z.enum(['chapter-15', 'finished']);
export type AdaptedBookCoinsMilestone = z.infer<typeof adaptedBookCoinsMilestoneSchema>;

/**
 * Coin amount to grant, driven by the `testCandyJarPromotion` unleash AB-payload so product can tune
 * it without a deploy. It is sent by the client, so the server treats it as untrusted input and clamps
 * it to a per-reward maximum (see the caps in candyjar.service.ts) — the client cannot farm coins by
 * inflating this value.
 */
export const candyJarCoinsSchema = z
  .number()
  .int()
  .nonnegative()
  .describe('Coins to grant, from the AB-test payload; clamped server-side to a max');

export const candyJarRouter = c.router(
  {
    claimSubscriberCoins: {
      summary:
        'Grant CandyJar (GalateaTV) coins to a Galatea subscriber (one-time) and mint a same-account deep-link token',
      method: 'POST',
      path: '/subscriber-coins/claim',
      body: z.object({ coins: candyJarCoinsSchema }),
      responses: {
        200: z.object({
          /** Coins granted now. 0 if already claimed or the caller is not an eligible subscriber. */
          coinsAdded: z.number(),
          /** Opaque single-use token to open CandyJar on the same account. */
          authToken: z.string().min(1),
        }),
      },
    },
    grantAdaptedBookCoins: {
      summary:
        'Grant the repeatable in-reader CandyJar coins for reaching a reading milestone on an adapted story, and mint a same-account deep-link token. Deduped per (user, story, milestone).',
      method: 'POST',
      path: '/adapted-book-coins/grant',
      body: z.object({
        storyId: z.string().min(1).describe('Galatea story (series) id the user is reading'),
        milestone: adaptedBookCoinsMilestoneSchema,
        coins: candyJarCoinsSchema,
      }),
      responses: {
        200: z.object({
          /** Coins granted now. 0 if already granted for this story+milestone, or the caller is ineligible. */
          coinsAdded: z.number(),
          /** Opaque single-use token to open CandyJar on the same account. */
          authToken: z.string().min(1),
        }),
      },
    },
    mintDeeplinkToken: {
      summary: 'Mint an opaque, single-use, short-lived token to open CandyJar on the same account as the authed user',
      method: 'POST',
      path: '/deeplink-token',
      body: c.noBody(),
      responses: {
        200: z.object({ authToken: z.string().min(1) }),
      },
    },
    showsForStories: {
      summary: 'Returns the CandyJar (GalateaTV) shows that exist for the given Galatea story ids',
      method: 'POST',
      path: '/shows-for-stories',
      body: z.object({
        storyIds: z.array(z.string()).max(20),
        language: languageCodesSchema.describe('Language code used to localize show title/cover'),
      }),
      responses: {
        200: z.object({ shows: CandyJarShow.array() }),
      },
    },
    promoSeriesForShow: {
      summary:
        'Resolves a single adapted CandyJar show (by GalateaTV series id) into the full promo-series payload — WITH playable episodes — for the in-app VideoPlayer opened from the books-to-movies shelf.',
      method: 'POST',
      path: '/promo-series',
      body: z.object({
        seriesId: z.number().int().positive().describe('GalateaTV series id (CandyJarShow.seriesId)'),
        language: languageCodesSchema.describe('Language code used to localize the series/episodes'),
      }),
      responses: {
        // `series` is null when the show has no free episodes / no localization for the language.
        200: z.object({ series: CJPromoShelfSeries.nullable() }),
      },
    },
  },
  { pathPrefix: '/candyjar' },
);
